import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { Surface, Btn, Field, Input, Select } from "./Primitives";
import { CurrencyDollar, Tag, Ticket, EnvelopeSimple, ChartLine, X } from "@phosphor-icons/react";

/* ============================================================
   Finance summary
   ============================================================ */
export function SuperuserFinanceCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/superuser/finance/summary")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Load failed"));
  }, []);
  if (err) return <Surface className="p-6 text-red-300 text-xs" data-testid="finance-card-error">{err}</Surface>;
  if (!data) return <Surface className="p-6 text-zinc-500 text-xs">Loading finance…</Surface>;
  return (
    <Surface className="p-6" data-testid="superuser-finance-card">
      <div className="flex items-center gap-2 mb-4">
        <ChartLine size={16} className="text-amber" />
        <h3 className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Revenue · forecast</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div data-testid="finance-mrr">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">MRR</div>
          <div className="text-2xl font-mono text-amber">{fmtMoney(data.mrr, "EUR")}</div>
        </div>
        <div data-testid="finance-arr">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">ARR</div>
          <div className="text-2xl font-mono text-amber">{fmtMoney(data.arr, "EUR")}</div>
        </div>
        <div data-testid="finance-arpa">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">ARPA</div>
          <div className="text-2xl font-mono">{fmtMoney(data.arpa, "EUR")}</div>
        </div>
        <div data-testid="finance-tenants">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">Active / Total</div>
          <div className="text-2xl font-mono">{data.active_tenants}<span className="text-zinc-500 text-lg"> / {data.total_tenants}</span></div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">By plan (MRR)</div>
          {Object.entries(data.by_plan_mrr).map(([p, v]) => (
            <div key={p} className="flex justify-between text-xs font-mono py-1 border-b border-zinc-900">
              <span className="text-zinc-400">{p}</span><span className="text-amber">{fmtMoney(v, "EUR")}</span>
            </div>
          ))}
          <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">By status (#)</div>
          {Object.entries(data.by_status_count).filter(([, v]) => v > 0).map(([s, v]) => (
            <div key={s} className="flex justify-between text-xs font-mono py-1 border-b border-zinc-900">
              <span className="text-zinc-400">{s}</span><span>{v}</span>
            </div>
          ))}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">12-month forecast (linear)</div>
          <div className="text-[10px] text-zinc-500 mb-3">
            +{fmtMoney(data.forecast_assumptions.new_mrr_per_month, "EUR")}/mo new MRR · trial-conv {data.trial_conversion_pct}% · {data.recent_signups_30d} signups in last 30d
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-1 text-[10px] font-mono">
            {data.forecast_12mo.map((f) => (
              <div key={f.month_offset} className="bg-zinc-950 border border-zinc-900 p-2">
                <div className="text-zinc-500">M+{f.month_offset}</div>
                <div className="text-amber">{fmtMoney(f.projected_mrr, "EUR")}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">Top tenants by MRR</div>
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="text-zinc-500 border-b border-zinc-900">
              <th className="text-left py-1.5">Name</th>
              <th className="text-left py-1.5">Plan</th>
              <th className="text-right py-1.5">Aircraft</th>
              <th className="text-right py-1.5">MRR</th>
              <th className="text-right py-1.5">Disc</th>
            </tr>
          </thead>
          <tbody>
            {data.top_tenants.slice(0, 10).map((t) => (
              <tr key={t.tenant_id} className="border-b border-zinc-900/60">
                <td className="py-1.5 text-zinc-300">{t.name}</td>
                <td className="py-1.5 text-zinc-400">{t.plan}/{t.cadence}</td>
                <td className="py-1.5 text-right text-zinc-400">{t.aircraft_count}</td>
                <td className="py-1.5 text-right text-amber">{fmtMoney(t.monthly_amount, "EUR")}</td>
                <td className="py-1.5 text-right text-zinc-500">{t.discount_pct ? `${t.discount_pct}%` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Surface>
  );
}

/* ============================================================
   Plan pricing
   ============================================================ */
export function SuperuserPlanPricingCard() {
  const [pricing, setPricing] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  const load = async () => {
    try { const r = await api.get("/superuser/plan-pricing"); setPricing(r.data); }
    catch (e) { setToast(`Load failed — ${e.response?.data?.detail || e.message}`); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit) return;
    setBusy(true);
    try {
      await api.patch("/superuser/plan-pricing", {
        plan: edit.plan, cadence: edit.cadence,
        list_amount: parseFloat(edit.list_amount || 0),
      });
      setEdit(null);
      setToast("Plan pricing updated.");
      setTimeout(() => setToast(""), 3500);
      load();
    } catch (e) {
      setToast(`Save failed — ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const reset = async (plan, cadence) => {
    if (!confirm(`Reset ${plan}/${cadence} to code default?`)) return;
    await api.patch("/superuser/plan-pricing", { plan, cadence, reset: true });
    setToast(`Reset ${plan}/${cadence}.`);
    setTimeout(() => setToast(""), 3500);
    load();
  };

  if (!pricing) return null;
  return (
    <Surface className="p-6" data-testid="plan-pricing-card">
      <div className="flex items-center gap-2 mb-4">
        <Tag size={16} className="text-amber" />
        <h3 className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Plan pricing ({pricing.currency})</h3>
        {pricing.updated_at && <span className="text-[10px] text-zinc-500 font-mono ml-auto">last edited {new Date(pricing.updated_at).toLocaleString()}</span>}
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-900">
          <th className="text-left py-2">Plan</th>
          <th className="text-left py-2">Cadence</th>
          <th className="text-right py-2">List price / aircraft</th>
          <th className="text-right py-2">Cents</th>
          <th></th>
        </tr></thead>
        <tbody>
          {Object.entries(pricing.pricing).flatMap(([plan, cads]) =>
            Object.entries(cads).map(([cad, row]) => (
              <tr key={`${plan}-${cad}`} className="border-b border-zinc-900/60" data-testid={`pricing-row-${plan}-${cad}`}>
                <td className="py-2 capitalize">{plan}</td>
                <td className="py-2 text-zinc-400">{cad}</td>
                <td className="py-2 text-right font-mono text-amber">
                  {fmtMoney(row.list_amount, "EUR")}
                  {row.is_override && <span className="ml-2 text-[10px] text-amber/60 uppercase">override</span>}
                </td>
                <td className="py-2 text-right font-mono text-zinc-500 text-xs">{row.unit_amount_cents}</td>
                <td className="py-2 text-right">
                  <Btn variant="ghost" onClick={() => setEdit({ plan, cadence: cad, list_amount: row.list_amount })}
                       data-testid={`pricing-edit-${plan}-${cad}`}>Edit</Btn>
                  {row.is_override && (
                    <button onClick={() => reset(plan, cad)} className="ml-2 text-[10px] text-zinc-500 hover:text-amber underline">Reset</button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {edit && (
        <div className="mt-4 p-4 border border-amber/30 bg-amber/5">
          <div className="text-xs font-mono mb-3 text-amber">Edit {edit.plan} · {edit.cadence}</div>
          <div className="flex gap-3 items-end">
            <Field label={`List ${pricing.currency} / aircraft / ${edit.cadence === "annual" ? "year" : "month"}`}>
              <Input type="number" step="0.01" value={edit.list_amount}
                     onChange={(e) => setEdit({ ...edit, list_amount: e.target.value })}
                     data-testid="pricing-edit-input" />
            </Field>
            <Btn onClick={save} disabled={busy} data-testid="pricing-edit-save">{busy ? "Saving…" : "Save"}</Btn>
            <Btn variant="ghost" onClick={() => setEdit(null)}>Cancel</Btn>
          </div>
        </div>
      )}
      {toast && <div className="mt-3 text-[11px] text-amber font-mono" data-testid="pricing-toast">{toast}</div>}
    </Surface>
  );
}

/* ============================================================
   Discount codes
   ============================================================ */
export function SuperuserDiscountCodesCard() {
  const [codes, setCodes] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const load = async () => { const r = await api.get("/superuser/discount-codes"); setCodes(r.data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form?.code || !form?.percent_off) { setToast("Code and percent_off required"); return; }
    setBusy(true);
    try {
      await api.post("/superuser/discount-codes", {
        code: (form.code || "").toUpperCase(),
        percent_off: parseInt(form.percent_off, 10),
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions, 10) : null,
        applies_to_plans: form.applies_to_plans || ["standard", "pro"],
        active: form.active !== false,
        description: form.description || null,
      });
      setForm(null);
      setToast("Discount code created.");
      setTimeout(() => setToast(""), 3500);
      load();
    } catch (e) {
      setToast(`Create failed — ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const toggleActive = async (c) => {
    await api.patch(`/superuser/discount-codes/${c.code}`, { active: !c.active });
    load();
  };
  const remove = async (c) => {
    if (!confirm(`Delete ${c.code}? Codes with redemptions are soft-deleted.`)) return;
    await api.delete(`/superuser/discount-codes/${c.code}`);
    load();
  };

  return (
    <Surface className="p-6" data-testid="discount-codes-card">
      <div className="flex items-center gap-2 mb-4">
        <Ticket size={16} className="text-amber" />
        <h3 className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Discount codes</h3>
        <Btn className="ml-auto" onClick={() => setForm({ code: "", percent_off: 10, applies_to_plans: ["standard", "pro"], active: true })}
             data-testid="discount-code-new-btn">+ New code</Btn>
      </div>
      {form && (
        <div className="mb-4 p-4 border border-amber/30 bg-amber/5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Code"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="LAUNCH20" data-testid="dc-code-input" /></Field>
            <Field label="Percent off"><Input type="number" min={1} max={100} value={form.percent_off} onChange={(e) => setForm({ ...form, percent_off: e.target.value })} data-testid="dc-pct-input" /></Field>
            <Field label="Valid from"><Input type="date" value={form.valid_from || ""} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} /></Field>
            <Field label="Valid until"><Input type="date" value={form.valid_until || ""} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></Field>
            <Field label="Max redemptions"><Input type="number" min={1} value={form.max_redemptions || ""} placeholder="∞" onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} /></Field>
            <div className="col-span-3"><Field label="Description"><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Launch promo — Q1 2026" /></Field></div>
          </div>
          <div className="mt-3 flex gap-2 items-center text-xs">
            <span className="text-zinc-500">Applies to:</span>
            {["standard", "pro"].map((p) => (
              <label key={p} className="flex items-center gap-1.5">
                <input type="checkbox" checked={(form.applies_to_plans || []).includes(p)}
                       onChange={(e) => {
                         const cur = new Set(form.applies_to_plans || []);
                         if (e.target.checked) cur.add(p); else cur.delete(p);
                         setForm({ ...form, applies_to_plans: [...cur] });
                       }} />
                {p}
              </label>
            ))}
          </div>
          <div className="mt-4 flex gap-2 justify-end">
            <Btn variant="ghost" onClick={() => setForm(null)}>Cancel</Btn>
            <Btn onClick={create} disabled={busy} data-testid="dc-save-btn">{busy ? "Creating…" : "Create code"}</Btn>
          </div>
        </div>
      )}
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-900">
          <th className="text-left py-2">Code</th>
          <th className="text-left py-2">% Off</th>
          <th className="text-left py-2">Plans</th>
          <th className="text-left py-2">Validity</th>
          <th className="text-right py-2">Used / Cap</th>
          <th className="text-left py-2">Status</th>
          <th></th>
        </tr></thead>
        <tbody data-testid="discount-codes-table">
          {(codes || []).map((c) => (
            <tr key={c.code} className="border-b border-zinc-900/60">
              <td className="py-2 font-mono text-amber">{c.code}</td>
              <td className="py-2">{c.percent_off}%</td>
              <td className="py-2 text-xs text-zinc-400">{(c.applies_to_plans || []).join(", ")}</td>
              <td className="py-2 text-[10px] text-zinc-500 font-mono">
                {c.valid_from?.slice(0, 10) || "—"} → {c.valid_until?.slice(0, 10) || "∞"}
              </td>
              <td className="py-2 text-right font-mono text-xs">{c.times_redeemed || 0} / {c.max_redemptions ?? "∞"}</td>
              <td className="py-2">
                <button onClick={() => toggleActive(c)} className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 border ${c.active ? "text-green-300 border-green-900/50 bg-green-950/30" : "text-zinc-500 border-zinc-800"}`} data-testid={`dc-toggle-${c.code}`}>{c.active ? "active" : "disabled"}</button>
              </td>
              <td className="py-2 text-right"><button onClick={() => remove(c)} className="text-zinc-500 hover:text-red-400" data-testid={`dc-delete-${c.code}`}><X size={14} /></button></td>
            </tr>
          ))}
          {codes && codes.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-zinc-500 text-sm">No codes yet.</td></tr>}
        </tbody>
      </table>
      {toast && <div className="mt-3 text-[11px] text-amber font-mono" data-testid="dc-toast">{toast}</div>}
    </Surface>
  );
}

/* ============================================================
   Invite client
   ============================================================ */
export function SuperuserInviteClientCard() {
  const [form, setForm] = useState({ plan: "standard", operating_region: "EU" });
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [invitations, setInvitations] = useState([]);

  useEffect(() => {
    api.get("/superuser/discount-codes").then((r) => setCodes((r.data || []).filter((c) => c.active)));
    api.get("/superuser/invitations").then((r) => setInvitations(r.data || []));
  }, []);

  const send = async () => {
    setBusy(true); setToast("");
    try {
      await api.post("/superuser/invite-client", {
        email: form.email,
        tenant_name: form.tenant_name,
        plan: form.plan,
        operating_region: form.operating_region,
        discount_code: form.discount_code || null,
      });
      setToast(`Invitation sent to ${form.email}.`);
      setForm({ plan: "standard", operating_region: "EU" });
      const r = await api.get("/superuser/invitations");
      setInvitations(r.data || []);
      setTimeout(() => setToast(""), 4000);
    } catch (e) {
      setToast(`Invite failed — ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  return (
    <Surface className="p-6" data-testid="invite-client-card">
      <div className="flex items-center gap-2 mb-4">
        <EnvelopeSimple size={16} className="text-amber" />
        <h3 className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Invite a client</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Email"><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" /></Field>
        <Field label="Workspace name"><Input value={form.tenant_name || ""} onChange={(e) => setForm({ ...form, tenant_name: e.target.value })} data-testid="invite-tenant-name" /></Field>
        <Field label="Plan">
          <Select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} data-testid="invite-plan">
            <option value="standard">Standard</option>
            <option value="pro">Pro</option>
          </Select>
        </Field>
        <Field label="Region">
          <Select value={form.operating_region} onChange={(e) => setForm({ ...form, operating_region: e.target.value })}>
            <option value="EU">EU</option>
            <option value="US">US</option>
            <option value="CA">CA</option>
            <option value="UK">UK</option>
          </Select>
        </Field>
        <div className="col-span-2">
          <Field label="Discount code (optional)">
            <Select value={form.discount_code || ""} onChange={(e) => setForm({ ...form, discount_code: e.target.value })} data-testid="invite-discount">
              <option value="">— No discount —</option>
              {codes.filter((c) => (c.applies_to_plans || []).includes(form.plan)).map((c) => (
                <option key={c.code} value={c.code}>{c.code} — {c.percent_off}% off</option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="col-span-2 flex items-end justify-end">
          <Btn onClick={send} disabled={busy || !form.email || !form.tenant_name} data-testid="invite-send">{busy ? "Sending…" : "Send invitation"}</Btn>
        </div>
      </div>
      {toast && <div className="mt-3 text-[11px] text-amber font-mono" data-testid="invite-toast">{toast}</div>}
      {invitations.length > 0 && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">Recent invitations</div>
          <table className="w-full text-xs font-mono">
            <thead><tr className="text-zinc-500 border-b border-zinc-900">
              <th className="text-left py-1.5">Email</th>
              <th className="text-left py-1.5">Workspace</th>
              <th className="text-left py-1.5">Plan</th>
              <th className="text-left py-1.5">Discount</th>
              <th className="text-left py-1.5">Status</th>
              <th className="text-left py-1.5">Sent</th>
            </tr></thead>
            <tbody data-testid="invitations-table">
              {invitations.slice(0, 15).map((i) => (
                <tr key={i.id} className="border-b border-zinc-900/60">
                  <td className="py-1.5 text-zinc-300">{i.email}</td>
                  <td className="py-1.5 text-zinc-400">{i.tenant_name}</td>
                  <td className="py-1.5 text-zinc-400">{i.plan}</td>
                  <td className="py-1.5 text-amber">{i.discount_code ? `${i.discount_code} (${i.discount_pct}%)` : "—"}</td>
                  <td className="py-1.5 text-zinc-500">{i.status}{i.email_sent === false ? " · email-skipped" : ""}</td>
                  <td className="py-1.5 text-zinc-500">{new Date(i.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Surface>
  );
}
